import api from './api'

/**
 * Serviço de Dashboard do Gestor (MANAGER)
 * 
 * Integrado com backend real em localhost:8080
 * Endpoints documentados em MANAGER_API_SPEC.md
 * 
 * Todos os endpoints aceitam parâmetros opcionais de data:
 *   { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }
 */
export default {
  /**
   * PDIs pendentes de aprovação do gestor
   * GET /api/manager/pdis/pending?startDate=&endDate=
   */
  getPendingPDIs(params = {}) {
    return api.get('/api/manager/pdis/pending', { params })
  },

  /**
   * Tarefas atrasadas no Jira da equipe
   * GET /api/manager/jira/overdue-tasks?startDate=&endDate=
   */
  getOverdueTasks(params = {}) {
    return api.get('/api/manager/jira/overdue-tasks', { params })
  },

  /**
   * Reuniões 1:1 agendadas nos próximos 7 dias
   * GET /api/manager/one-on-ones/upcoming?startDate=&endDate=
   */
  getUpcomingOneOnOnes(params = {}) {
    return api.get('/api/manager/one-on-ones/upcoming', { params })
  },

  /**
   * Ações pendentes do gestor
   * GET /api/manager/pending-actions
   */
  getPendingActions(params = {}) {
    return api.get('/api/manager/pending-actions', { params })
  },

  /**
   * Estatísticas de produtividade e risco
   * GET /api/manager/team/productivity?startDate=&endDate=
   */
  getProductivityStats(params = {}) {
    return api.get('/api/manager/team/productivity', { params })
  },

  /**
   * Próximas reuniões 1:1 (lista detalhada)
   * GET /api/manager/one-on-ones/next
   */
  getNextOneOnOnes(params = {}) {
    return api.get('/api/manager/one-on-ones/next', { params })
  },

  /**
   * Estatísticas de campanhas de avaliação
   * GET /api/manager/campaigns/stats?startDate=&endDate=
   */
  getCampaignStats(params = {}) {
    return api.get('/api/manager/campaigns/stats', { params })
  },

  /**
   * Metas e feedbacks
   * GET /api/manager/goals-feedback/stats?startDate=&endDate=
   */
  getGoalsFeedbackStats(params = {}) {
    return api.get('/api/manager/goals-feedback/stats', { params })
  },

  /**
   * Estatísticas de competências do time
   * GET /api/manager/competencies-stats
   */
  getCompetenciesStats(params = {}) {
    return api.get('/api/manager/competencies-stats', { params })
  },

  /**
   * Estatísticas de feedbacks do time
   * GET /api/manager/feedbacks-stats?startDate=&endDate=
   */
  getFeedbacksStats(params = {}) {
    return api.get('/api/manager/feedbacks-stats', { params })
  },

  /**
   * Avaliações atrasadas
   * GET /api/manager/overdue-evaluations
   */
  getOverdueEvaluations(params = {}) {
    return api.get('/api/manager/overdue-evaluations', { params })
  },

  /**
   * Análise inteligente com recomendações (IA) - endpoint legado
   * GET /api/manager/intelligent-analysis
   */
  getIntelligentAnalysis(params = {}) {
    return api.get('/api/manager/intelligent-analysis', { params })
  },

  /**
   * Gerar insights com IA (novo endpoint)
   * POST /api/ai/insights/generate
   */
  generateAiInsights(body = {}) {
    return api.post('/api/ai/insights/generate', body)
  },

  /**
   * Buscar insights já gerados (rápido, sem geração)
   * GET /api/ai/insights?limit=5&teamId=&userId=
   */
  getAiInsights(params = {}) {
    return api.get('/api/ai/insights', { params })
  },

  /**
   * Velocidade do time (tarefas por semana)
   * GET /api/manager/team-velocity?startDate=&endDate=
   */
  getTeamVelocity(params = {}) {
    return api.get('/api/manager/team-velocity', { params })
  },

  /**
   * Métricas de engenharia (4 cards: entrega, qualidade, colaboração, evolução)
   * GET /api/manager/engineering-metrics?startDate=&endDate=
   */
  getEngineeringMetrics(params = {}) {
    return api.get('/api/manager/engineering-metrics', { params })
  },

  /**
   * Competências de engenharia (radar chart)
   * GET /api/manager/engineering-competencies
   */
  getEngineeringCompetencies(params = {}) {
    return api.get('/api/manager/engineering-competencies', { params })
  },

  /**
   * Métricas Git/Jira (PRs, commits, reviews, tempo médio PR)
   * GET /api/manager/git-metrics?startDate=&endDate=
   */
  getGitMetrics(params = {}) {
    return api.get('/api/manager/git-metrics', { params })
  },

  /**
   * Listar membros da equipe (liderados do gestor)
   * GET /api/manager/team/members
   */
  getTeamMembers() {
    return api.get('/api/manager/team/members')
  },

  /**
   * Listar PDIs de todos os liderados
   * GET /api/manager/team/pdis?status=&userId=&startDate=&endDate=
   */
  getTeamPdis(params = {}) {
    return api.get('/api/manager/team/pdis', { params })
  },

  /**
   * Recuperar métricas favoritas do gestor
   * GET /api/manager/favorite-metrics
   * @returns {{ metricIds: string[] }}
   */
  getFavoriteMetrics() {
    return api.get('/api/manager/favorite-metrics')
  },

  /**
   * Salvar métricas favoritas do gestor
   * POST /api/manager/favorite-metrics
   * @param {string[]} metricIds - Lista de IDs das métricas favoritadas
   * @returns {{ success: boolean, metricIds: string[] }}
   */
  saveFavoriteMetrics(metricIds) {
    return api.post('/api/manager/favorite-metrics', { metricIds })
  },

  /**
   * Buscar dados reais de uma métrica específica
   * GET /api/manager/metric-data/{metricId}?startDate=&endDate=
   * @param {string} metricId - ID da métrica (ex: 'github-prs-semana')
   * @param {Object} params - { startDate, endDate } opcionais
   * @returns {{ labels: string[], datasets: Array<{ label: string, data: number[] }> }}
   */
  getMetricData(metricId, params = {}) {
    return api.get(`/api/manager/metric-data/${metricId}`, { params })
  },

  /**
   * Análise individual de um usuário
   * GET /api/manager/users/{userId}/individual-analysis?startDate=&endDate=
   * @param {string} userId - ID do usuário
   * @param {Object} params - { startDate, endDate } opcionais
   */
  getIndividualAnalysis(userId, params = {}) {
    return api.get(`/api/manager/users/${userId}/individual-analysis`, { params })
  },

  // ===================================================
  // DASHBOARD MANAGER V2 — /api/manager/v2/
  // ===================================================

  /** DORA: deployFrequency, leadTime, changeFailure, mttr, reworkRate */
  getDoraMetrics(params = {}) {
    return api.get('/api/manager/v2/dora-metrics', { params })
  },

  /** Gargalos: PR stage breakdown, PRs parados, WIP envelhecido */
  getFlowBottlenecks(params = {}) {
    return api.get('/api/manager/v2/flow-bottlenecks', { params })
  },

  /** Alocação de investimento por tipo de issue (Jira) */
  getInvestmentAllocation(params = {}) {
    return api.get('/api/manager/v2/investment-allocation', { params })
  },

  /** Previsibilidade: confiança, scope creep, risco, burndown */
  getSprintPredictability(params = {}) {
    return api.get('/api/manager/v2/sprint-predictability', { params })
  },

  /** Qualidade: SonarQube (mock até integração) */
  getQualityMetrics(params = {}) {
    return api.get('/api/manager/v2/quality-metrics', { params })
  },

  /** Copilot: adoção, aceitação, impacto (mock até integração) */
  getCopilotMetrics(params = {}) {
    return api.get('/api/manager/v2/copilot-metrics', { params })
  },

  /** On-call: quem está de plantão agora (mock PagerDuty) */
  getOnCallStatus(params = {}) {
    return api.get('/api/manager/v2/on-call', { params })
  },

  /** Scorecard de saúde por time: todas as dimensões DORA por squad */
  getTeamScorecard(params = {}) {
    return api.get('/api/manager/v2/team-scorecard', { params })
  },
}
